'use client';

import Link from 'next/link';
import Image from 'next/image';
import classes from './HorizontalImageCard.module.scss';
import { Button, Container, Grid } from '@mantine/core';
import { useAOS } from '@/src/lib/hooks/useAOS';
import { Carousel } from '@mantine/carousel';

type HorizontalImageCardUI = {
  variant?: 'light' | 'dark';
  showSeparator?: boolean;

  radius?: 'sm' | 'md' | 'lg' | 'xl';
  imageRadius?: 'sm' | 'md' | 'lg' | 'xl';

  containerMaxWidth?: string; // "1100px"
  paddingY?: string; // "4.5rem"
  gap?: string; // "2rem"

  /** Desktop card height (image + text column) */
  height?: string; // e.g. "520px" | "60vh"
  /** Optional: different image height. Defaults to ui.height */
  imageHeight?: string;

  /** Image fit */
  imageFit?: 'cover' | 'contain';

  /** If true: description area scrolls instead of overflowing */
  enableScroll?: boolean;

  /** If enableScroll=false: clamp lines */
  maxLines?: number;
};

export type HorizontalImageCardProps = {
  type?: 'horizontal-image-card';
  title: string;
  description: string | string[];
  button?: string;
  buttonHref?: string;
  position: 'left' | 'right';
  images: CardImage[];
  imageAlt?: string;

  ui?: HorizontalImageCardUI;
};

const radiusToCssVar = (r?: HorizontalImageCardUI['radius']) => {
  switch (r) {
    case 'sm':
      return 'var(--hic-radius-sm)';
    case 'md':
      return 'var(--hic-radius-md)';
    case 'lg':
      return 'var(--hic-radius-lg)';
    case 'xl':
      return 'var(--hic-radius-xl)';
    default:
      return 'var(--hic-radius-lg)';
  }
};

type CardImage = {
  src: string;
  alt: string;
};

export const HorizontalImageCard = ({ data }: { data: HorizontalImageCardProps }) => {
  useAOS();

  const { title, description, button, buttonHref, position, images, imageAlt, ui } = data;

  const variant = ui?.variant ?? 'light';
  const showSeparator = ui?.showSeparator ?? true;

  const height = ui?.height ?? '520px';
  const imageHeight = ui?.imageHeight ?? height;

  const enableScroll = ui?.enableScroll ?? true;
  const imageFit = ui?.imageFit ?? 'cover';

  const styleVars: React.CSSProperties = {
    ['--hic-container-max' as any]: ui?.containerMaxWidth ?? '1100px',
    ['--hic-padding-y' as any]: ui?.paddingY ?? '4.5rem',
    ['--hic-gap' as any]: ui?.gap ?? '2rem',

    ['--hic-radius' as any]: radiusToCssVar(ui?.radius),
    ['--hic-image-radius' as any]: radiusToCssVar(ui?.imageRadius ?? ui?.radius),

    ['--hic-height' as any]: height,
    ['--hic-image-height' as any]: imageHeight,
    ['--hic-image-fit' as any]: imageFit,

    ['--hic-max-lines' as any]: String(ui?.maxLines ?? 8),
  };

  return (
    <section
      className={[
        classes.wrapper,
        variant === 'dark' ? classes.dark : classes.light,
        showSeparator ? classes.withSeparator : classes.noSeparator,
      ].join(' ')}
      style={styleVars}
    >
      <Container data-aos="zoom-in" className={classes.container} style={{ maxWidth: 'var(--hic-container-max)' }}>
        <Grid gutter="lg" className={classes.grid} style={{ flexDirection: position === 'left' ? 'row-reverse' : 'row' }}>
          {/* IMAGE */}
          <Grid.Col span={{ base: 12, sm: 6 }} className={classes.imageCol} data-aos="flip-up" order={{ base: 1, sm: position === 'left' ? 2 : 1 }}>
            <h2 className={classes.titleMobile}>{title}</h2>
            <div className={classes.imageWrap}>
              <Carousel
                withIndicators
                loop
                draggable
                slideGap="md"
                align="start"
                className={classes.carousel}
                styles={{
                  indicator: { backgroundColor: '#c9a456' },
                }}
              >
                {data.images.map((img, i) => (
                  <Carousel.Slide key={i}>
                    <div className={classes.slide}>
                      <Image
                        src={img.src}
                        alt={img.alt ?? title}
                        fill
                        sizes="(max-width: 768px) 100vw, 50vw"
                        className={classes.image}
                        priority={i === 0}
                        loading={i === 0 ? 'eager' : 'lazy'}
                      />
                    </div>
                  </Carousel.Slide>
                ))}
              </Carousel>
            </div>
          </Grid.Col>

          {/* TEXT */}
          <Grid.Col span={{ base: 12, sm: 6 }} className={classes.textCol} order={{ base: 2, sm: position === 'left' ? 1 : 2 }}>
            <div className={classes.textBox}>
              <h2 className={classes.title}>{title}</h2>

              <div className={[classes.middle, enableScroll ? classes.scroll : classes.clamp].join(' ')}>
                <div className={classes.description}>
                  {Array.isArray(description)
                    ? description.map((p, i) => (
                      <p key={i} className={classes.paragraph}>
                        {p}
                      </p>
                    ))
                    : (
                      <p className={classes.paragraph}>{description}</p>
                    )}
                </div>
              </div>

              <div className={classes.bottom}>
                {button && (
                  <Button component={Link} href={buttonHref ?? ''} size="md" className={classes.button}>
                    {button}
                  </Button>
                )}
              </div>
            </div>
          </Grid.Col>
        </Grid>
      </Container>
    </section>
  );
};
